import { applySharedAccessibilityDomState } from './shared'
import type { AccessibilityFontSize, AccessibilityLineSpacing } from './shared'

export interface AdminAccessibilitySettings {
  fontSize: AccessibilityFontSize
  highContrast: boolean
  reduceMotion: boolean
  dyslexiaFont: boolean
  screenReaderOptimized: boolean
  lineSpacing: AccessibilityLineSpacing
}

const STORAGE_KEY = 'admin-accessibility-settings'

const DEFAULT_SETTINGS: AdminAccessibilitySettings = {
  fontSize: 'medium',
  highContrast: false,
  reduceMotion: false,
  dyslexiaFont: false,
  screenReaderOptimized: false,
  lineSpacing: 'normal',
}

export function getAdminAccessibilitySettings(): AdminAccessibilitySettings {
  if (typeof window === 'undefined') return DEFAULT_SETTINGS

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) }
    }
  } catch (error) {
    console.error('Failed to load admin accessibility settings:', error)
  }

  return DEFAULT_SETTINGS
}

export function saveAdminAccessibilitySettings(
  settings: Partial<AdminAccessibilitySettings>,
): void {
  if (typeof window === 'undefined') return

  try {
    const current = getAdminAccessibilitySettings()
    const updated = { ...current, ...settings }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
  } catch (error) {
    console.error('Failed to save admin accessibility settings:', error)
  }
}

export function applyAdminAccessibilitySettings(settings: AdminAccessibilitySettings): void {
  applySharedAccessibilityDomState(settings)
}

export function resetAdminAccessibilitySettings(): void {
  if (typeof window === 'undefined') return

  try {
    sessionStorage.removeItem(STORAGE_KEY)
    applyAdminAccessibilitySettings(DEFAULT_SETTINGS)
  } catch (error) {
    console.error('Failed to reset admin accessibility settings:', error)
  }
}

export function initializeAdminAccessibilitySettings(): void {
  const settings = getAdminAccessibilitySettings()
  applyAdminAccessibilitySettings(settings)
}
